import { Fragment, SyntheticEvent } from 'react';
import { observer } from 'mobx-react';
import cls from 'classnames';
import { useToggle } from '@cutwise/react-utils';
import { isNumber } from '@cutwise/utils';

import Label, { SIZE_LARGE, SIZE_MEDIUM } from '@/common/components/Label/Label';
import { CollectionItem } from '@/drive/entities/DriveItem/CollectionItem';
import { FolderItem } from '@/drive/entities/DriveItem/FolderItem';
import { getAccessStatusTitle } from '@/security/components/AccessStatus/AccessStatus.constants';
import DriveGlobalAccessModal from '@/security/components/GlobalAccessModal/DriveGlobalAccessModal/DriveGlobalAccessModal';
import { getAccessById, getDriveAccessAppearance } from '@/security/constants/access';
import { GlobalAccessLevel } from '@/security/constants/GlobalAccessLevel';

import styles from './DriveContainerAccessStatus.module.css';

export type DriveAccessContainer = FolderItem | CollectionItem;

export interface DriveContainerAccessStatusProps {
  item: DriveAccessContainer;
  size?: typeof SIZE_MEDIUM | typeof SIZE_LARGE;
  isCreatable?: boolean;
  className?: string;
  shouldHidePrivate?: boolean;
  onUpdate?: () => void;
}

function DriveContainerAccessStatus(props: DriveContainerAccessStatusProps) {
  const { item, size = SIZE_LARGE, isCreatable, className, shouldHidePrivate, onUpdate } = props;

  const [isAccessModalOpen, openAccessModal, closeAccessModal] = useToggle();

  if (!isNumber(item.access) || (shouldHidePrivate && item.access === GlobalAccessLevel.Restricted)) {
    return null;
  }

  const type = item instanceof FolderItem ? 'driveFolder' : 'collection';
  const isEditable = !!isCreatable && item.canManageAccess;

  function onClickHandler(e: SyntheticEvent) {
    if (!isEditable) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    openAccessModal();
  }

  return (
    <Fragment>
      <Label
        appearance={getDriveAccessAppearance(item.access)}
        aria-label="Access status"
        className={cls({ [styles.interactive]: isEditable }, className)}
        onClick={onClickHandler}
        size={size}
        title={getAccessStatusTitle(type, item.access)}
      >
        {getAccessById(item.access, type)?.title}
      </Label>

      <DriveGlobalAccessModal
        target={item}
        type={type}
        isOpen={isAccessModalOpen}
        onClose={closeAccessModal}
        onSuccess={onUpdate}
      />
    </Fragment>
  );
}

export default observer(DriveContainerAccessStatus);
